import React, { useMemo } from 'react';
import './FormStyles.css';

export const formatJson = value => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  return JSON.stringify(JSON.parse(text), null, 2);
};

export default function JsonTextArea({
  label,
  name,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  placeholder = '',
  rows = 8,
  className = '',
}) {
  const jsonState = useMemo(() => {
    if (!String(value || '').trim()) return 'empty';
    try {
      JSON.parse(value);
      return 'valid';
    } catch {
      return 'invalid';
    }
  }, [value]);

  const emitValue = (nextValue, sourceEvent = {}) => {
    onChange?.({
      ...sourceEvent,
      target: { name, value: nextValue, type: 'textarea' },
      currentTarget: { name, value: nextValue, type: 'textarea' },
    });
  };

  const formatCurrentValue = event => {
    try {
      emitValue(formatJson(value), event);
    } catch {
      // Keep unfinished JSON unchanged so the user can correct it.
    }
  };

  const handlePaste = event => {
    const pasted = event.clipboardData?.getData('text') || '';
    try {
      const formatted = formatJson(pasted);
      event.preventDefault();
      emitValue(formatted, event);
    } catch {
      // Allow ordinary paste for partial JSON fragments.
    }
  };

  const handleKeyDown = event => {
    if (event.key !== 'Tab') return;
    event.preventDefault();
    const element = event.currentTarget;
    const start = element.selectionStart;
    const end = element.selectionEnd;
    const nextValue = `${value.slice(0, start)}  ${value.slice(end)}`;
    emitValue(nextValue, event);
    requestAnimationFrame(() => {
      element.selectionStart = start + 2;
      element.selectionEnd = start + 2;
    });
  };

  return (
    <div className={`form-group json-editor-group ${className}`}>
      <div className="json-editor-heading">
        {label && <label htmlFor={name} className="form-label">{label}{required && <span className="required">*</span>}</label>}
        <div className="json-editor-tools">
          <span className={`json-status ${jsonState}`}>{jsonState === 'valid' ? '✓ Valid JSON' : jsonState === 'invalid' ? 'Invalid JSON' : 'JSON'}</span>
          <button type="button" onClick={formatCurrentValue} disabled={disabled || jsonState !== 'valid'}>Format JSON</button>
        </div>
      </div>
      <textarea id={name} name={name} value={value || ''} onChange={onChange}
        onBlur={formatCurrentValue} onPaste={handlePaste} onKeyDown={handleKeyDown}
        disabled={disabled} placeholder={placeholder} rows={rows} required={required}
        spellCheck="false" data-marathi-input="false"
        className={`form-textarea json-editor ${error || jsonState === 'invalid' ? 'error' : ''}`} />
      {error && <div className="form-error">{error}</div>}
      {!error && jsonState === 'invalid' && <div className="form-error">JSON अपूर्ण किंवा चुकीचा आहे.</div>}
      <small className="form-help">Paste किंवा field मधून बाहेर गेल्यावर JSON आपोआप format होईल. Indent साठी Tab वापरा.</small>
    </div>
  );
}
